// 6-step pipeline progress indicator. Reads `progress` map keyed by step id.
// Shows live elapsed time for the currently-running step.
import { useEffect, useState } from 'react';
import { PIPELINE_STEPS } from '../lib/orchestrator.js';

export default function ReviewProgress({ progress }) {
  // Track the start time of the currently-running step locally. Tick every
  // 250ms while one is running so the user sees progress.
  const [runningStartedAt, setRunningStartedAt] = useState(null);
  const [tick, setTick] = useState(0);

  const runningStepId = Object.entries(progress).find(
    ([, v]) => v?.status === 'start'
  )?.[0];

  useEffect(() => {
    if (runningStepId && !runningStartedAt) {
      setRunningStartedAt(Date.now());
    } else if (!runningStepId) {
      setRunningStartedAt(null);
    }
  }, [runningStepId, runningStartedAt]);

  useEffect(() => {
    if (!runningStepId) return;
    const id = setInterval(() => setTick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, [runningStepId]);

  // Reset tick + start time when the running step changes.
  useEffect(() => {
    setRunningStartedAt(runningStepId ? Date.now() : null);
    setTick(0);
  }, [runningStepId]);

  const elapsedRunning = runningStartedAt ? (Date.now() - runningStartedAt) / 1000 : 0;
  void tick;

  return (
    <ol className="space-y-2">
      {PIPELINE_STEPS.map((step, idx) => {
        const state = progress[step.id]?.status || 'pending';
        const dur = progress[step.id]?.durationMs;
        const err = progress[step.id]?.error;
        const isRunning = state === 'start';
        return (
          <li
            key={step.id}
            className={`flex items-center gap-3 rounded-md border px-3 py-2 transition ${
              state === 'error'
                ? 'border-red-900/60 bg-red-900/10'
                : isRunning
                ? 'border-gold/60 bg-navy-900'
                : 'border-navy-700 bg-navy-900'
            }`}
          >
            <Marker state={state} number={idx + 1} />
            <div className="flex-1">
              <div className="text-sm font-medium">{step.label}</div>
              {err && <div className="mt-0.5 text-xs text-red-300">Error: {err}</div>}
            </div>
            <div className="text-right text-xs">
              {state === 'start' && (
                <span className="text-gold">{elapsedRunning.toFixed(1)}s…</span>
              )}
              {state === 'done' && (
                <span className="text-slate-400">
                  Done <span className="text-slate-500">({((dur || 0) / 1000).toFixed(1)}s)</span>
                </span>
              )}
              {state === 'skipped' && (
                <span className="text-slate-500">Cached ✓</span>
              )}
              {state === 'error' && <span className="text-red-400">Failed</span>}
              {state === 'pending' && <span className="text-slate-500">Waiting</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Marker({ state, number }) {
  const base =
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition';
  if (state === 'done') return <span className={`${base} bg-gold text-navy-900`}>✓</span>;
  if (state === 'skipped') return <span className={`${base} bg-navy-700 text-slate-400`}>✓</span>;
  if (state === 'start')
    return (
      <span className={`${base} animate-pulse border-2 border-gold text-gold`}>{number}</span>
    );
  if (state === 'error') return <span className={`${base} bg-red-700 text-white`}>!</span>;
  return <span className={`${base} border border-navy-700 text-slate-400`}>{number}</span>;
}
